import { Overlay } from '@angular/cdk/overlay';
import {
    HTTP_INTERCEPTORS,
    provideHttpClient,
    withInterceptorsFromDi
} from '@angular/common/http';
import {
    ErrorHandler,
    NgModule,
    RendererFactory2,
    TemplateRef,
    inject,
    provideAppInitializer
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ApiModule, PreferencesModule } from '@bilancio/api';
import {
    AuthModule,
    HeaderAuthInterceptor,
    JwtService,
    RefreshAuthInterceptor
    // UnauthRedirectInterceptor
} from '@bilancio/auth';
import {
    AuthActions,
    RootStoreModule,
    SuperStore,
    extModulesForDev,
    extModulesForProd
} from '@bilancio/root-store';
import {
    AppShellComponent,
    AppShellDrawerComponent,
    AppShellDrawerItemComponent,
    AppShellHeaderComponent,
    AppShellMainComponent,
    AppShellSidebarComponent,
    AppShellSidebarItemComponent,
    AppShellSidebarItemNoLinkComponent,
    BalloonComponent,
    IconComponent,
    LoaderCountComponent,
    ModalComponent,
    ModalPopuComponent,
    ToastComponent
} from '@bilancio/ui';
import { Store } from '@ngrx/store';
import {
    AuthInterceptor,
    EmbedNavigationDirective,
    HTTP_CODES_TOKEN,
    HttpCodesInterceptor,
    ONE_FRONT_CONFIG,
    ProxyInterceptor
} from '@vapor/angular-sdk';
import { HelpCenterComponent, HelpCenterService } from '@vapor/angular-sdk-ui';
import { JoyrideModule } from 'ngx-joyride';
import {
    NgxPermissionsConfigurationService,
    NgxPermissionsModule
} from 'ngx-permissions';
import { filter } from 'rxjs/operators';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ChangeOfficeModule } from './components/change-office/change-office.module';
import { ChangeOfficeService } from './components/change-office/change-office.service';
import { FooterDrawerModule } from './components/footer-drawer/footer-drawer.module';
import { HelpComponent } from './components/help/help.component';
import { NoteRilascioComponent } from './components/note-rilascio/note-rilascio.component';
import { TourTemplateComponent } from './components/tour-template/tour-template.component';
import { UserContextMenuModule } from './components/user-context-menu/user-context-menu.module';
import { WhitePageModule } from './components/white-page/white-page.module';
import { ForbiddenMessageInterceptor } from './core/interceptors/forbidden-message.interceptor';
import { HeaderCorsInterceptor } from './core/interceptors/header-cors.interceptor';
import { ReloadUserPermitsInterceptor } from './core/interceptors/user-permits.interceptor';
import { ErrorHandlerService } from './core/service/error.service';
import { URLS_PROVIDER } from './core/service/url.service';
import { AGM_CONFIG, agmConfig } from './core/tokens/agm.token';
import {
    AMCHART_LIC_CONFIG,
    amchartLicConfig
} from './core/tokens/amcharts.token';
import {
    APP_INSIGHTS_CONFIG,
    appInsightsConfig
} from './core/tokens/appInsights.token';
import {
    CHANGE_SCHEMA_COOP_CONFIG,
    changeSchemaCoopConfig
} from './core/tokens/change-schema-coop.token';
import {
    CHECKUP_URL_CONFIG,
    checkupUrlConfig
} from './core/tokens/checkup.token';
import {
    CONNECTION_CONFIG,
    connectionConfig
} from './core/tokens/connection.token';
import {
    CONSOLIDATED_CONFIG,
    consolidatedConfig
} from './core/tokens/consolidated-conf.token';
import {
    CORRELATION_VISIBLE_CONFIG,
    correlationVisibleConfig
} from './core/tokens/correlation.token';
import {
    DATA_ORIGIN_CONFIG,
    dataOriginConfig
} from './core/tokens/data-origin.token';
import {
    DEBUG_TOOL_CONFIG,
    debugToolConfig
} from './core/tokens/debug-tools.token';
import {
    DEPOSIT_VISIBLE_CONFIG,
    depositVisibleConfig
} from './core/tokens/deposit.token';
import {
    DIGITAL_VISIBLE_CONFIG,
    digitalVisibleConfig
} from './core/tokens/digital.token';

import { ETS_CONFIG, etsConfig } from './core/tokens/ets.token';
import {
    LOGIN_PATH_CONFIG,
    loginPathConfig
} from './core/tokens/login-path.token';
import {
    MODEFINANCE_VISIBLE_CONFIG,
    modefinanceVisibleConfig
} from './core/tokens/modefinance.token';
import {
    NEW_BE_GET_CONFIG,
    newBeTokenConfig
} from './core/tokens/new-be-get.token';
import { NEW_YEAR_FD, newYearFDConfig } from './core/tokens/new-year-fd.token';
import {
    ONE_FRONT_BIL_CONFIG,
    oneFrontBilConfig
} from './core/tokens/one-front.token';
import {
    TAXONOMY_SHOW_CONFIG,
    taxonomyShowConfig
} from './core/tokens/taxonomy-show.token';
import { TSDESK_CONFIG, tsDeskConfig } from './core/tokens/ts-desk.token';
import {
    TSINTRO_VIDEO_LINK,
    TsIntroVideoConfig
} from './core/tokens/tsintro-video.token';
import { TSLAND_LINK, TsLinkLandConfig } from './core/tokens/tslinkland.token';
import {
    XBRL_INDEX_AI_ACTION_CONFIG,
    xbrlIndexAIActionConfig
} from './core/tokens/xbrl-index-ai-action.token';
import { InterframeGlobalModule } from './interframe-global/interframe-global.module';

const MY_ONE_FRONT_CONFIG = {
    one: {
        embed: {
            param: oneFrontBilConfig.embedParam
        },
        auth: {
            token: {
                verify: true,
                refresh: false,
                keepAlive: 5000,
                params: '',
                storage: {
                    key: oneFrontBilConfig.storageKey
                }
            },
            retry: 1,
            retryDelay: 500
        },
        login: {
            target: {
                url: 'loginApp',
                params: ['mode=redirect', `appId=${oneFrontBilConfig.appId}`]
            }
        },
        proxy: {
            baseUrl: oneFrontBilConfig.proxyBaseUrl
        },
        rest: {
            header: {
                accessToken: {
                    key: 'Authorization',
                    value: 'Bearer {TOKEN}'
                },
                property: {
                    name: 'headers'
                }
            },
            url: {
                property: {
                    name: 'url'
                }
            }
        },
        helpDesk: {
            url: tsDeskConfig.tsDeskApi
        }
    },
    pages: {
        login: `/${loginPathConfig.loginPath}`,
        accessDenied: `/${loginPathConfig.loginPath}`
    }
};

/**
 * Modulo di partenza del programma
 *
 * @export
 * @class AppModule
 */
@NgModule({
    declarations: [AppComponent],
    bootstrap: [AppComponent],
    imports: [
        BrowserModule,
        JoyrideModule.forRoot(),
        TourTemplateComponent,
        ApiModule.forRoot(URLS_PROVIDER),
        AppRoutingModule,
        AuthModule.forRoot({
            authRootUrl: `${connectionConfig.protocol}//${connectionConfig.host}:${connectionConfig.port}/api/Auth`,
            authDigitalUrl: `${connectionConfig.protocol}//${connectionConfig.host}:${connectionConfig.port}/api/TsDigital`,
            // deepcode ignore HardcodedNonCryptoSecret: <this is the name of cookie token, not hardcoded token>
            jwtKeyName: 'BilancioSecurityToken',
            loginPath: loginPathConfig.loginPath,
            oneFrontStorageKey: oneFrontBilConfig.storageKey
        }),
        ChangeOfficeModule,
        LoaderCountComponent,
        ModalComponent,
        NgxPermissionsModule.forRoot(),
        NoteRilascioComponent,
        FooterDrawerModule,
        AppShellHeaderComponent,
        AppShellSidebarComponent,
        AppShellDrawerItemComponent,
        AppShellSidebarItemComponent,
        AppShellMainComponent,
        AppShellDrawerComponent,
        AppShellComponent,
        AppShellSidebarItemNoLinkComponent,
        IconComponent,
        UserContextMenuModule,
        EmbedNavigationDirective,
        ToastComponent,
        RootStoreModule.forRoot(),
        debugToolConfig.isDebugTools ? extModulesForDev : extModulesForProd,
        PreferencesModule,
        ModalPopuComponent,
        BalloonComponent,
        HelpComponent,
        WhitePageModule,
        HelpCenterComponent,
        InterframeGlobalModule
    ],
    providers: [
        Overlay,
        ChangeOfficeService,
        HelpCenterService,
        { provide: ErrorHandler, useClass: ErrorHandlerService },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ProxyInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpCodesInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderCorsInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HeaderAuthInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RefreshAuthInterceptor,
            multi: true
        },
        // {
        //     provide: HTTP_INTERCEPTORS,
        //     useClass: UnauthRedirectInterceptor,
        //     multi: true
        // },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ForbiddenMessageInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ReloadUserPermitsInterceptor,
            multi: true
        },
        // {
        //     provide: APP_INITIALIZER,
        //     useFactory: () => () => {
        //         const key = 'v';
        //         // Legge da querystring il parametro v
        //         const regex = new RegExp(`[\\?&]${key}=([^&#]*)`);
        //         const results = regex.exec(location.search);
        //         const qsValue =
        //             results === null
        //                 ? ''
        //                 : decodeURIComponent(results[1].replace(/\+/g, ' '));
        //         const v = version.replace(/(\.|\-)/g, '');
        //         // Se il parametro v non è aggiunto o se il valore è diverso dall'hash della versione lo riaggiungo
        //         if (!qsValue || qsValue !== v) {
        //             // Aggiunge il parametro e ricarica la pagina
        //             const kvp = document.location.search
        //                 .substr(1)
        //                 .split('&')
        //                 .filter(item => item !== '');
        //             let i = kvp.length;
        //             const value = encodeURI(v);
        //             while (i--) {
        //                 // Se il parametro è in lista ne sostituisco il valore
        //                 const x = kvp[i].split('=');
        //                 if (x[0] === key) {
        //                     x[1] = value;
        //                     kvp[i] = x.join('=');
        //                     break;
        //                 }
        //             }
        //             // Se il parametro non è in lista lo aggiungo
        //             if (i < 0) {
        //                 kvp[kvp.length] = [key, value].join('=');
        //             }
        //             document.location.search = kvp.join('&');
        //         }
        //     },
        //     multi: true
        // },
        provideAppInitializer(() => {
            const initializerFn = (
                (
                    permissionsConfigurationService: NgxPermissionsConfigurationService,
                    rendererFactory: RendererFactory2
                ) =>
                () => {
                    // Use rendererFactory service to create renderer
                    // @see https://stackoverflow.com/a/47833320
                    const renderer2 = rendererFactory.createRenderer(
                        null,
                        null
                    );
                    permissionsConfigurationService.addPermissionStrategy(
                        'disable',
                        (templateRef: TemplateRef<Element>) => {
                            renderer2.setAttribute(
                                templateRef.elementRef.nativeElement
                                    .nextSibling,
                                'disabled',
                                'true'
                            );
                        }
                    );
                    permissionsConfigurationService.addPermissionStrategy(
                        'enable',
                        (templateRef: TemplateRef<Element>) => {
                            renderer2.removeAttribute(
                                templateRef.elementRef.nativeElement
                                    .nextSibling,
                                'disabled'
                            );
                        }
                    );
                }
            )(
                inject(NgxPermissionsConfigurationService),
                inject(RendererFactory2)
            );
            return initializerFn();
        }),
        provideAppInitializer(() => {
            const initializerFn = (
                (jwtService: JwtService, store: SuperStore) => () => {
                    // subscribe dispatch action only when user logged in
                    jwtService.isTokenLoaded$
                        .asObservable()
                        .pipe(filter((flag: any) => flag))
                        .subscribe(() => {
                            store.dispatch(AuthActions.readJwtUserToken());
                        });
                    if (jwtService.get()) {
                        // needed to manage page refresh
                        store.dispatch(AuthActions.readJwtUserToken());
                    }
                }
            )(inject(JwtService), inject(Store));
            return initializerFn();
        }),
        {
            provide: CHANGE_SCHEMA_COOP_CONFIG,
            useValue: changeSchemaCoopConfig
        },
        {
            provide: XBRL_INDEX_AI_ACTION_CONFIG,
            useValue: xbrlIndexAIActionConfig
        },
        { provide: CONSOLIDATED_CONFIG, useValue: consolidatedConfig },
        { provide: TAXONOMY_SHOW_CONFIG, useValue: taxonomyShowConfig },
        { provide: ETS_CONFIG, useValue: etsConfig },
        {
            provide: MODEFINANCE_VISIBLE_CONFIG,
            useValue: modefinanceVisibleConfig
        },
        {
            provide: CORRELATION_VISIBLE_CONFIG,
            useValue: correlationVisibleConfig
        },
        { provide: DATA_ORIGIN_CONFIG, useValue: dataOriginConfig },
        {
            provide: DIGITAL_VISIBLE_CONFIG,
            useValue: digitalVisibleConfig
        },
        {
            provide: DEPOSIT_VISIBLE_CONFIG,
            useValue: depositVisibleConfig
        },
        { provide: AGM_CONFIG, useValue: agmConfig },
        { provide: AMCHART_LIC_CONFIG, useValue: amchartLicConfig },
        { provide: NEW_YEAR_FD, useValue: newYearFDConfig },
        { provide: NEW_BE_GET_CONFIG, useValue: newBeTokenConfig },
        { provide: LOGIN_PATH_CONFIG, useValue: loginPathConfig },
        { provide: CHECKUP_URL_CONFIG, useValue: checkupUrlConfig },
        { provide: TSINTRO_VIDEO_LINK, useValue: TsIntroVideoConfig },
        { provide: TSLAND_LINK, useValue: TsLinkLandConfig },
        { provide: ONE_FRONT_CONFIG, useValue: MY_ONE_FRONT_CONFIG },
        { provide: ONE_FRONT_BIL_CONFIG, useValue: oneFrontBilConfig },
        { provide: DEBUG_TOOL_CONFIG, useValue: debugToolConfig },
        { provide: APP_INSIGHTS_CONFIG, useValue: appInsightsConfig },
        { provide: CONNECTION_CONFIG, useValue: connectionConfig },
        { provide: TSDESK_CONFIG, useValue: tsDeskConfig },
        { provide: HTTP_CODES_TOKEN, useValue: [401] },
        provideHttpClient(withInterceptorsFromDi())
    ]
})
export class AppModule {}
